import React, { useState, useEffect, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { tradingService } from '../../services/tradingService';

// Quadrant → accent colour. Leading=green, Weakening=amber, Lagging=red, Improving=blue.
const QUAD_COLOR = {
  Leading:   '#16a34a',
  Weakening: '#f59e0b',
  Lagging:   '#dc2626',
  Improving: '#3b82f6',
};
const QUAD_ORDER = ['Leading', 'Improving', 'Weakening', 'Lagging'];

const TAIL_OPTIONS   = [5, 8, 10, 12, 15];
const WINDOW_OPTIONS = [8, 10, 12, 14];

const SectorRotationPage = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [tail, setTail]       = useState(10);
  const [window_, setWindow]  = useState(12);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tradingService.getSectorRRG({ tail, window: window_ });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [tail, window_]);

  useEffect(() => { load(); }, [load]);

  const sectors = (data?.sectors || []).filter(s => s.tail && s.tail.length > 0);

  // Axis ranges from all tail points, padded, guaranteed to straddle 100.
  const allR = sectors.flatMap(s => s.tail.map(p => p.rs_ratio));
  const allM = sectors.flatMap(s => s.tail.map(p => p.rs_momentum));
  const pad = 0.15;
  const rng = (arr) => {
    if (!arr.length) return [98, 102];
    let lo = Math.min(...arr, 100), hi = Math.max(...arr, 100);
    const p = Math.max((hi - lo) * pad, 0.3);
    return [lo - p, hi + p];
  };
  const [xlo, xhi] = rng(allR);
  const [ylo, yhi] = rng(allM);

  const fade = (hex) => hex + '55'; // ~33% alpha for the trailing tail

  const traces = sectors.map(s => {
    const qc = QUAD_COLOR[s.quadrant] || '#9ca3af';
    const xs = s.tail.map(p => p.rs_ratio);
    const ys = s.tail.map(p => p.rs_momentum);
    const n = xs.length;
    return {
      x: xs, y: ys,
      mode: 'lines+markers+text',
      type: 'scatter',
      name: s.sector,
      line: { color: fade(qc), width: 1.2, shape: 'spline' },
      marker: {
        size: xs.map((_, i) => (i === n - 1 ? 14 : 4)),
        color: qc,
        line: { color: '#ffffff', width: xs.map((_, i) => (i === n - 1 ? 1.3 : 0)) },
      },
      text: xs.map((_, i) => (i === n - 1 ? s.symbol : '')),
      textposition: 'top center',
      textfont: { size: 10, color: qc, family: 'monospace' },
      hovertemplate: `<b>${s.sector}</b> (${s.symbol})<br>RS-Ratio %{x:.2f}<br>RS-Mom %{y:.2f}<extra></extra>`,
      showlegend: false,
    };
  });

  const rect = (x0, x1, y0, y1, color) => ({
    type: 'rect', xref: 'x', yref: 'y', x0, x1, y0, y1,
    fillcolor: color, line: { width: 0 }, layer: 'below',
  });

  const shapes = [
    rect(100, xhi, 100, yhi, 'rgba(22,163,74,0.06)'),   // Leading  (TR)
    rect(100, xhi, ylo, 100, 'rgba(245,158,11,0.06)'),  // Weakening(BR)
    rect(xlo, 100, ylo, 100, 'rgba(220,38,38,0.06)'),   // Lagging  (BL)
    rect(xlo, 100, 100, yhi, 'rgba(59,130,246,0.06)'),  // Improving(TL)
    { type: 'line', xref: 'x', yref: 'y', x0: 100, x1: 100, y0: ylo, y1: yhi, line: { color: 'rgba(150,150,150,0.5)', width: 1, dash: 'dot' } },
    { type: 'line', xref: 'x', yref: 'y', x0: xlo, x1: xhi, y0: 100, y1: 100, line: { color: 'rgba(150,150,150,0.5)', width: 1, dash: 'dot' } },
  ];

  const corner = (x, y, text, color, xa, ya) => ({
    x, y, xref: 'x', yref: 'y', text, showarrow: false,
    font: { size: 11, color, family: 'Georgia, serif' },
    xanchor: xa, yanchor: ya, opacity: 0.7,
  });
  const annotations = [
    corner(xhi, yhi, 'LEADING',   QUAD_COLOR.Leading,   'right', 'top'),
    corner(xhi, ylo, 'WEAKENING', QUAD_COLOR.Weakening, 'right', 'bottom'),
    corner(xlo, ylo, 'LAGGING',   QUAD_COLOR.Lagging,   'left',  'bottom'),
    corner(xlo, yhi, 'IMPROVING', QUAD_COLOR.Improving, 'left',  'top'),
  ];

  const Select = ({ label, value, onChange, options }) => (
    <label className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
      {label}
      <select
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded px-2 py-1 text-gray-700 dark:text-gray-200"
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-serif text-gray-800 dark:text-gray-100">Sector Rotation</h2>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            Relative strength of the 11 SPDR sectors vs SPY · weekly · RRG-style
            {data?.as_of ? ` · as of ${data.as_of}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select label="Tail (wks)" value={tail} onChange={setTail} options={TAIL_OPTIONS} />
          <Select label="Smoothing" value={window_} onChange={setWindow} options={WINDOW_OPTIONS} />
          <button
            onClick={load}
            className="text-[11px] border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 px-3 py-1.5 rounded-lg transition-colors"
          >
            {loading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && <p className="text-[12px] text-red-400 mb-2">{error}</p>}
      {loading && !data && <p className="text-[12px] text-gray-500 animate-pulse">Computing sector rotation…</p>}

      {sectors.length > 0 && (
        <>
          <div className="border border-gray-200 dark:border-gray-800 rounded-xl p-2 bg-white dark:bg-[#0d1117]">
            <Plot
              data={traces}
              layout={{
                height: 560,
                margin: { t: 10, b: 44, l: 50, r: 14 },
                paper_bgcolor: 'transparent',
                plot_bgcolor: 'transparent',
                font: { family: 'monospace', size: 10, color: '#6b7280' },
                xaxis: { range: [xlo, xhi], title: { text: 'RS-Ratio  (relative strength →)', font: { size: 10 } }, showgrid: false, zeroline: false, color: '#6b7280' },
                yaxis: { range: [ylo, yhi], title: { text: 'RS-Momentum  (improving ↑)', font: { size: 10 } }, showgrid: false, zeroline: false, color: '#6b7280' },
                shapes,
                annotations,
                hovermode: 'closest',
                showlegend: false,
              }}
              config={{ displayModeBar: false, scrollZoom: false }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          </div>

          {/* Read-out table — quadrant + coordinates per sector */}
          <div className="mt-4 overflow-x-auto scrollbar-thin">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-200 dark:border-gray-800">
                  <th className="py-1.5 pr-3 font-normal uppercase tracking-wider text-[10px]">Sector</th>
                  <th className="py-1.5 pr-3 font-normal uppercase tracking-wider text-[10px]">ETF</th>
                  <th className="py-1.5 pr-3 font-normal uppercase tracking-wider text-[10px]">Quadrant</th>
                  <th className="py-1.5 pr-3 font-normal uppercase tracking-wider text-[10px] text-right">RS-Ratio</th>
                  <th className="py-1.5 pr-3 font-normal uppercase tracking-wider text-[10px] text-right">RS-Mom</th>
                </tr>
              </thead>
              <tbody>
                {[...sectors]
                  .sort((a, b) => QUAD_ORDER.indexOf(a.quadrant) - QUAD_ORDER.indexOf(b.quadrant) || (b.rs_ratio - a.rs_ratio))
                  .map(s => (
                    <tr key={s.symbol} className="border-b border-gray-100 dark:border-gray-900">
                      <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-200">{s.sector}</td>
                      <td className="py-1.5 pr-3 text-gray-500 font-mono">{s.symbol}</td>
                      <td className="py-1.5 pr-3">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-block w-2 h-2 rounded-full" style={{ background: QUAD_COLOR[s.quadrant] || '#9ca3af' }} />
                          <span style={{ color: QUAD_COLOR[s.quadrant] || '#9ca3af' }}>{s.quadrant}</span>
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 text-right font-mono text-gray-600 dark:text-gray-300">{s.rs_ratio?.toFixed(2)}</td>
                      <td className="py-1.5 pr-3 text-right font-mono text-gray-600 dark:text-gray-300">{s.rs_momentum?.toFixed(2)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <p className="text-[10px] text-gray-400 mt-3 leading-relaxed">
            <span className="text-green-500">Leading</span> = outperforming &amp; improving (hold OW) ·
            <span className="text-blue-500"> Improving</span> = laggard turning up (early OW) ·
            <span className="text-amber-500"> Weakening</span> = outperforming but fading (trim OW) ·
            <span className="text-red-500"> Lagging</span> = underperforming &amp; falling (UW).
            Sectors rotate clockwise. Monitoring lens only — does not drive allocation.
          </p>
        </>
      )}
    </div>
  );
};

export default SectorRotationPage;
